import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { CreditCard, Bank, PaperPlaneTilt, Copy, Check, DeviceMobile, EnvelopeSimple, UploadSimple, Download, QrCode, Folder, Plus, Trash, FileText, Eye, Link, Clock, ArrowClockwise } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { PageHero } from '../components/motion/PageHero';
import {
  createPaymentLink,
  listPaymentLinks,
  sendPaymentLink,
  getPaymentLinkQr,
  getCompanyBankDetails,
  updateCompanyBankDetails,
  uploadCompanyRib,
  downloadCompanyRib,
  sendBankTransferDetails,
  getDossiers,
  createQuote,
  listQuotes,
  downloadQuote,
  viewQuote,
  sendQuote,
  extendQuote,
  deleteQuote,
} from '../services/api';

function formatMoney(v, currency = 'EUR') {
  const n = Number(v);
  if (!isFinite(n)) return '—';
  const cur = (currency || 'EUR').toUpperCase();
  // XPF est zero-decimal
  const opts = { style: 'currency', currency: cur };
  if (cur === 'XPF') { opts.minimumFractionDigits = 0; opts.maximumFractionDigits = 0; }
  try {
    return new Intl.NumberFormat('fr-FR', opts).format(n);
  } catch {
    return `${n} ${cur}`;
  }
}

export default function PaymentSenderPage() {
  const [searchParams] = useSearchParams();
  const initialDossier = searchParams.get('dossier_id') || '';
  const initialTab = searchParams.get('mode') === 'virement' ? 'virement' : 'stripe';
  const [tab, setTab] = useState(initialTab);
  return (
    <div className="space-y-6 animate-fade-in" data-testid="payment-sender-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Envoi de paiement"
        subtitle="Génération et transmission des liens de paiement personnalisés"
        icon={<PaperPlaneTilt size={24} weight="duotone" className="text-white" />}
        gradient="from-[#FF8E00] to-[#D5007D]"
        accents={['#EA3C3A', '#00AEEF']}
      />

      <div>
        <h1 className="text-3xl font-bold text-[#1A2421] tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Envoi de paiement
        </h1>
        <p className="text-[#5E6D66] mt-1">
          Générer un lien Stripe ou envoyer les coordonnées de virement bancaire (par email + SMS).
          {initialDossier && <> Rattaché au dossier <span className="font-mono">{initialDossier.slice(0,8)}...</span></>}
        </p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="stripe" data-testid="tab-stripe"><CreditCard size={16} className="mr-2" /> Lien Stripe</TabsTrigger>
          <TabsTrigger value="devis" data-testid="tab-devis"><FileText size={16} className="mr-2" /> Devis</TabsTrigger>
          <TabsTrigger value="virement" data-testid="tab-virement"><Bank size={16} className="mr-2" /> Virement bancaire</TabsTrigger>
        </TabsList>
        <TabsContent value="stripe" className="mt-4"><StripeLinkPanel initialDossierId={initialDossier} /></TabsContent>
        <TabsContent value="devis" className="mt-4"><QuotesPanel /></TabsContent>
        <TabsContent value="virement" className="mt-4"><BankTransferPanel initialDossierId={initialDossier} /></TabsContent>
      </Tabs>
    </div>
  );
}

function StripeLinkPanel({ initialDossierId = '' }) {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('eur');
  const [description, setDescription] = useState('');
  const [dossierId, setDossierId] = useState(initialDossierId);
  const [dossierQuery, setDossierQuery] = useState('');
  const [dossiers, setDossiers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [currentLink, setCurrentLink] = useState(null);
  const [qrUrl, setQrUrl] = useState('');
  const [links, setLinks] = useState([]);
  const [emailsRaw, setEmailsRaw] = useState('');
  const [phonesRaw, setPhonesRaw] = useState('');
  const [customSubject, setCustomSubject] = useState('');
  const [customSmsBody, setCustomSmsBody] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  // Mode détaillé multi-lignes
  const [mode, setMode] = useState('simple'); // 'simple' | 'detailed'
  const [invoiceLines, setInvoiceLines] = useState([{ description: '', quantity: 1, unit_price_ht: '', tva_rate: 20 }]);

  const detailedTotal = useMemo(() => {
    let ht = 0, tva = 0;
    for (const ln of invoiceLines) {
      const qty = Number(ln.quantity) || 0;
      const pu = Number(ln.unit_price_ht) || 0;
      const rate = (Number(ln.tva_rate) || 0) / 100;
      ht += qty * pu;
      tva += qty * pu * rate;
    }
    return { ht: Math.round(ht * 100) / 100, tva: Math.round(tva * 100) / 100, ttc: Math.round((ht + tva) * 100) / 100 };
  }, [invoiceLines]);

  const addLine = () => setInvoiceLines([...invoiceLines, { description: '', quantity: 1, unit_price_ht: '', tva_rate: 20 }]);
  const removeLine = (i) => setInvoiceLines(invoiceLines.filter((_, idx) => idx !== i));
  const updateLine = (i, key, value) => setInvoiceLines(invoiceLines.map((ln, idx) => idx === i ? { ...ln, [key]: value } : ln));

  const loadLinks = async () => {
    try {
      const r = await listPaymentLinks({ limit: 20 });
      setLinks(r.data.items || []);
    } catch {}
  };
  useEffect(() => { loadLinks(); }, []);
  useEffect(() => {
    getDossiers({ search: dossierQuery, limit: 15 }).then((r) => setDossiers(r.data.items || [])).catch(() => setDossiers([]));
  }, [dossierQuery]);

  const selectedDossier = useMemo(() => dossiers.find((d) => d.id === dossierId), [dossiers, dossierId]);

  const loadQr = async (id) => {
    try {
      const r = await getPaymentLinkQr(id, 10);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'image/png' }));
      setQrUrl(url);
    } catch {
      setQrUrl('');
    }
  };

  const selectLink = async (l) => {
    setCurrentLink(l);
    await loadQr(l.id);
  };

  const handleCreate = async () => {
    if (mode === 'detailed') {
      const validLines = invoiceLines.filter((ln) => ln.description && Number(ln.unit_price_ht) > 0);
      if (validLines.length === 0) {
        toast.warning('Ajoutez au moins une ligne détaillée valide');
        return;
      }
      if (detailedTotal.ttc <= 0) {
        toast.warning('Total TTC doit être > 0');
        return;
      }
      setCreating(true);
      try {
        const payload = {
          amount: detailedTotal.ttc,
          currency,
          description: description.trim() || validLines.map((l) => l.description).join(' + ').slice(0, 250),
          dossier_id: dossierId || undefined,
          invoice_lines: validLines.map((l) => ({
            description: l.description,
            quantity: Number(l.quantity),
            unit_price_ht: Number(l.unit_price_ht),
            tva_rate: Number(l.tva_rate),
          })),
        };
        const r = await createPaymentLink(payload);
        setCurrentLink(r.data);
        await loadQr(r.data.id);
        toast.success(`Lien Stripe créé (TTC: ${detailedTotal.ttc} ${currency.toUpperCase()})`);
        loadLinks();
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Erreur création');
      } finally { setCreating(false); }
      return;
    }
    if (!amount || Number(amount) <= 0) {
      toast.warning('Montant requis');
      return;
    }
    if (!description.trim()) {
      toast.warning('Description requise');
      return;
    }
    setCreating(true);
    try {
      const r = await createPaymentLink({ amount: Number(amount), currency, description, dossier_id: dossierId || undefined });
      setCurrentLink(r.data);
      await loadQr(r.data.id);
      toast.success('Lien Stripe créé');
      loadLinks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création');
    } finally {
      setCreating(false);
    }
  };

  const handleSend = async () => {
    if (!currentLink) return;
    const emails = emailsRaw.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
    const phones = phonesRaw.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
    if (emails.length === 0 && phones.length === 0) {
      toast.warning('Au moins un email ou un mobile');
      return;
    }
    setSending(true);
    try {
      const r = await sendPaymentLink(currentLink.id, {
        emails, phones,
        subject: customSubject || undefined,
        sms_body: customSmsBody || undefined,
      });
      toast.success(`Envoyé : ${r.data.emails} email(s), ${r.data.phones} SMS`);
      setEmailsRaw(''); setPhonesRaw('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi');
    } finally {
      setSending(false);
    }
  };

  const copyLink = () => {
    if (!currentLink?.checkout_url) return;
    navigator.clipboard.writeText(currentLink.checkout_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="border-[#E8E6E1] lg:col-span-2">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center gap-2 border-b border-[#E8E6E1] pb-3">
            <Label className="text-xs uppercase tracking-wide text-[#5E6D66]">Mode</Label>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setMode('simple')}
                className={`px-3 py-1 rounded-md text-xs border transition-colors ${mode === 'simple' ? 'bg-[#2D5A4C] text-white border-[#2D5A4C]' : 'bg-white border-[#E8E6E1]'}`}
                data-testid="mode-simple-btn"
              >Simple</button>
              <button
                type="button"
                onClick={() => setMode('detailed')}
                className={`px-3 py-1 rounded-md text-xs border transition-colors ${mode === 'detailed' ? 'bg-[#2D5A4C] text-white border-[#2D5A4C]' : 'bg-white border-[#E8E6E1]'}`}
                data-testid="mode-detailed-btn"
              >Détaillé (multi-lignes)</button>
            </div>
          </div>
          {mode === 'simple' ? (
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-sm">Montant</Label>
              <Input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 border-[#E8E6E1]" data-testid="stripe-amount" />
            </div>
            <div>
              <Label className="text-sm">Devise</Label>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="mt-1 w-full border border-[#E8E6E1] rounded-md p-2 text-sm bg-white" data-testid="stripe-currency">
                <option value="eur">EUR — Euro</option>
                <option value="usd">USD — US Dollar</option>
                <option value="gbp">GBP — Livre sterling</option>
                <option value="xpf">XPF — F CFP (Pacifique)</option>
              </select>
            </div>
            <div>
              <Label className="text-sm">Description</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Cotisation 2026" className="mt-1 border-[#E8E6E1]" data-testid="stripe-description" />
            </div>
          </div>
          ) : (
          <div className="space-y-3" data-testid="detailed-lines-section">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Devise</Label>
                <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="mt-1 w-full border border-[#E8E6E1] rounded-md p-2 text-sm bg-white" data-testid="stripe-currency-detailed">
                  <option value="eur">EUR — Euro</option>
                  <option value="usd">USD — US Dollar</option>
                  <option value="gbp">GBP — Livre sterling</option>
                  <option value="xpf">XPF — F CFP (Pacifique)</option>
                </select>
              </div>
              <div>
                <Label className="text-sm">Titre du paiement</Label>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="ex: Prestations conseil - Q4 2026" className="mt-1 border-[#E8E6E1]" data-testid="stripe-description-detailed" />
              </div>
            </div>
            <div className="border border-[#E8E6E1] rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-[#F0EFEB]">
                  <tr>
                    <th className="text-left p-2 w-6/12">Description</th>
                    <th className="text-left p-2 w-1/12">Qté</th>
                    <th className="text-left p-2 w-2/12">PU HT</th>
                    <th className="text-left p-2 w-2/12">TVA %</th>
                    <th className="text-left p-2 w-1/12">Total HT</th>
                    <th className="w-8 p-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceLines.map((ln, i) => {
                    const lineHt = (Number(ln.quantity) || 0) * (Number(ln.unit_price_ht) || 0);
                    return (
                      <tr key={i} className="border-t border-[#E8E6E1]" data-testid={`invoice-line-${i}`}>
                        <td className="p-1">
                          <Input value={ln.description} onChange={(e) => updateLine(i, 'description', e.target.value)} placeholder="Prestation..." className="border-[#E8E6E1] h-8 text-xs" data-testid={`line-description-${i}`} />
                        </td>
                        <td className="p-1">
                          <Input type="number" min="1" value={ln.quantity} onChange={(e) => updateLine(i, 'quantity', e.target.value)} className="border-[#E8E6E1] h-8 text-xs w-16" data-testid={`line-quantity-${i}`} />
                        </td>
                        <td className="p-1">
                          <Input type="number" step="0.01" min="0" value={ln.unit_price_ht} onChange={(e) => updateLine(i, 'unit_price_ht', e.target.value)} className="border-[#E8E6E1] h-8 text-xs" placeholder="0.00" data-testid={`line-pu-${i}`} />
                        </td>
                        <td className="p-1">
                          <select value={ln.tva_rate} onChange={(e) => updateLine(i, 'tva_rate', Number(e.target.value))} className="border border-[#E8E6E1] rounded h-8 text-xs w-full bg-white" data-testid={`line-tva-${i}`}>
                            <option value={0}>0%</option>
                            <option value={5.5}>5,5%</option>
                            <option value={8.5}>8,5% (DOM)</option>
                            <option value={10}>10%</option>
                            <option value={11}>11% (NC/WF)</option>
                            <option value={20}>20% (métro)</option>
                          </select>
                        </td>
                        <td className="p-1 text-right font-mono text-xs">
                          {lineHt.toFixed(2)}
                        </td>
                        <td className="p-1 text-center">
                          {invoiceLines.length > 1 && (
                            <button type="button" onClick={() => removeLine(i)} className="text-red-600 hover:text-red-800" data-testid={`remove-line-${i}`}>
                              <Trash size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-[#F9F8F5] border-t border-[#E8E6E1]">
                    <td colSpan={4} className="p-2 text-right text-xs text-[#5E6D66]">Sous-total HT</td>
                    <td className="p-2 text-right font-semibold" data-testid="detailed-total-ht">{detailedTotal.ht.toFixed(2)}</td>
                    <td></td>
                  </tr>
                  <tr className="bg-[#F9F8F5]">
                    <td colSpan={4} className="p-2 text-right text-xs text-[#5E6D66]">TVA totale</td>
                    <td className="p-2 text-right font-semibold" data-testid="detailed-total-tva">{detailedTotal.tva.toFixed(2)}</td>
                    <td></td>
                  </tr>
                  <tr className="bg-[#2D5A4C] text-white">
                    <td colSpan={4} className="p-2 text-right text-xs">Total TTC</td>
                    <td className="p-2 text-right font-bold" data-testid="detailed-total-ttc">{detailedTotal.ttc.toFixed(2)} {currency.toUpperCase()}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <Button type="button" size="sm" variant="outline" onClick={addLine} className="border-[#E8E6E1]" data-testid="add-line-btn">
              <Plus size={14} className="mr-1" /> Ajouter une ligne
            </Button>
          </div>
          )}
          <div>
            <Label className="text-xs flex items-center gap-1"><Folder size={12} /> Rattacher à un dossier (optionnel)</Label>
            <div className="flex gap-2 mt-1">
              <Input value={dossierQuery} onChange={(e) => setDossierQuery(e.target.value)} placeholder="Rechercher numéro/objet..." className="border-[#E8E6E1] flex-1" data-testid="stripe-dossier-search" />
              {dossierId && (
                <Button size="sm" variant="outline" onClick={() => setDossierId('')} className="border-[#E8E6E1] text-red-600" data-testid="stripe-clear-dossier">✕</Button>
              )}
            </div>
            {dossierId && selectedDossier && (
              <Badge className="mt-1 bg-[#2D5A4C]/10 text-[#2D5A4C] text-[10px]"><Folder size={10} className="mr-1" /> {selectedDossier.numero_dossier} — {selectedDossier.type_dossier}</Badge>
            )}
            {!dossierId && dossierQuery && dossiers.length > 0 && (
              <div className="mt-1 max-h-24 overflow-y-auto border border-[#E8E6E1] rounded-md bg-white">
                {dossiers.slice(0, 5).map((d) => (
                  <button key={d.id} type="button" onClick={() => { setDossierId(d.id); setDossierQuery(''); }} className="w-full text-left p-2 hover:bg-[#F9F8F5] text-xs border-b border-[#E8E6E1]" data-testid={`stripe-dossier-${d.id}`}>
                    <span className="font-medium">{d.numero_dossier}</span> — <span className="text-[#5E6D66]">{d.objet_besoin || d.type_dossier}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate} disabled={creating} className="bg-[#635BFF] hover:bg-[#4a44cc] text-white" data-testid="create-stripe-link-btn">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard size={16} className="mr-2" />}
              Générer le lien Stripe
            </Button>
            {mode === 'detailed' && (
              <Button onClick={() => setQuoteDialogOpen(true)} variant="outline" className="border-[#2D5A4C] text-[#2D5A4C] hover:bg-[#2D5A4C]/10" data-testid="create-quote-btn">
                <FileText size={16} className="mr-2" /> Générer devis PDF
              </Button>
            )}
          </div>

          {mode === 'detailed' && quoteDialogOpen && (
            <QuoteDialog
              lines={invoiceLines}
              currency={currency}
              title={description}
              dossierId={dossierId}
              onClose={() => setQuoteDialogOpen(false)}
            />
          )}

          {currentLink && (
            <div className="border border-[#E8E6E1] rounded-md p-3 bg-[#F9F8F5] space-y-2" data-testid="stripe-link-preview">
              <div className="flex items-start gap-3">
                {qrUrl && (
                  <img src={qrUrl} alt="QR" className="w-24 h-24 border border-[#E8E6E1] rounded-md bg-white p-1 flex-shrink-0" data-testid="stripe-link-qr" />
                )}
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div>
                  <p className="text-sm font-semibold">{formatMoney(currentLink.amount, (currentLink.currency || 'EUR').toUpperCase())} — {currentLink.description}</p>
                      <p className="text-[10px] text-[#5E6D66] font-mono truncate">{currentLink.session_id}</p>
                      {currentLink.dossier_id && (
                        <Badge className="mt-1 bg-[#2D5A4C]/10 text-[#2D5A4C] text-[10px]"><Folder size={10} className="mr-1" /> {currentLink.dossier_id.slice(0, 8)}</Badge>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button size="sm" variant="outline" onClick={copyLink} className="border-[#E8E6E1]">
                        {copied ? <Check size={14} className="mr-1 text-emerald-600" /> : <Copy size={14} className="mr-1" />}
                        {copied ? 'Copié' : 'Copier lien'}
                      </Button>
                      {qrUrl && (
                        <a href={qrUrl} download={`qr_stripe_${currentLink.id.slice(0, 8)}.png`} className="inline-flex">
                          <Button size="sm" variant="outline" className="border-[#E8E6E1] w-full" data-testid="stripe-download-qr">
                            <QrCode size={14} className="mr-1" /> QR PNG
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="pt-2 border-t border-[#E8E6E1] space-y-2">
                <div>
                  <Label className="text-xs flex items-center gap-1"><EnvelopeSimple size={12} /> Emails destinataires (séparés par virgule)</Label>
                  <Textarea rows={2} value={emailsRaw} onChange={(e) => setEmailsRaw(e.target.value)} placeholder="client1@x.fr, client2@y.fr" className="mt-1 border-[#E8E6E1] text-xs" data-testid="stripe-emails" />
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1"><DeviceMobile size={12} /> Mobiles destinataires (SMS Brevo, séparés par virgule)</Label>
                  <Textarea rows={2} value={phonesRaw} onChange={(e) => setPhonesRaw(e.target.value)} placeholder="06 12 34 56 78, +33698765432" className="mt-1 border-[#E8E6E1] text-xs" data-testid="stripe-phones" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Sujet email (optionnel)</Label>
                    <Input value={customSubject} onChange={(e) => setCustomSubject(e.target.value)} className="mt-1 border-[#E8E6E1] text-xs" data-testid="stripe-subject" />
                  </div>
                  <div>
                    <Label className="text-xs">SMS personnalisé (≤459 car., optionnel)</Label>
                    <Input value={customSmsBody} onChange={(e) => setCustomSmsBody(e.target.value)} maxLength={459} className="mt-1 border-[#E8E6E1] text-xs" data-testid="stripe-sms" />
                  </div>
                </div>
                <Button onClick={handleSend} disabled={sending} className="bg-[#2D5A4C] hover:bg-[#1E4136] w-full" data-testid="stripe-send-btn">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PaperPlaneTilt size={16} className="mr-2" />}
                  Envoyer le lien
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-[#E8E6E1] lg:col-span-1">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-2">Derniers liens</h3>
          <div className="space-y-2 max-h-[520px] overflow-y-auto">
            {links.length === 0 && <p className="text-xs text-[#5E6D66]">Aucun lien créé</p>}
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => selectLink(l)}
                className="w-full text-left p-2 rounded-md border border-[#E8E6E1] hover:bg-[#F9F8F5] transition-colors"
                data-testid={`link-item-${l.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{formatMoney(l.amount, (l.currency || 'EUR').toUpperCase())}</span>
                  <Badge className={l.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-800 text-[10px]' : 'bg-gray-100 text-gray-700 text-[10px]'}>
                    {l.payment_status || 'pending'}
                  </Badge>
                </div>
                <p className="text-[11px] text-[#5E6D66] truncate">{l.description}</p>
                {l.dossier_id && (
                  <p className="text-[10px] text-[#2D5A4C] mt-1"><Folder size={9} className="inline mr-1" />Dossier lié</p>
                )}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function BankTransferPanel({ initialDossierId = '' }) {
  const [details, setDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [emails, setEmails] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [dossierId] = useState(initialDossierId);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await getCompanyBankDetails();
      setDetails(r.data || {});
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await updateCompanyBankDetails({
        holder_name: details.holder_name || '',
        iban: (details.iban || '').replace(/\s/g, ''),
        bic: details.bic || '',
        bank_name: details.bank_name || '',
        address: details.address || '',
      });
      setDetails(r.data);
      toast.success('Coordonnées enregistrées');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur sauvegarde');
    }
    setSaving(false);
  };

  const uploadRib = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await uploadCompanyRib(fd);
      toast.success('RIB PDF sauvegardé');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur upload');
    }
    setUploading(false);
  };

  const downloadRib = async () => {
    try {
      const r = await downloadCompanyRib();
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = details.rib_filename || 'RIB_OSCOP.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur téléchargement');
    }
  };

  const send = async () => {
    const emailsList = emails.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
    if (emailsList.length === 0) {
      toast.warning('Au moins un email');
      return;
    }
    if (!details.iban) {
      toast.warning('IBAN manquant — configurez d\'abord vos coordonnées');
      return;
    }
    setSending(true);
    try {
      const r = await sendBankTransferDetails({
        emails: emailsList,
        amount: amount ? Number(amount) : undefined,
        description: description || undefined,
        dossier_id: dossierId || undefined,
      });
      toast.success(`Envoyé à ${r.data.emails} destinataire(s) ${r.data.rib_attached ? '(RIB joint)' : ''}`);
      setEmails(''); setAmount(''); setDescription('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi');
    }
    setSending(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-4 space-y-3">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Bank size={16} className="text-[#2D5A4C]" />
            Coordonnées bancaires de la société
          </h3>
          <div>
            <Label className="text-xs">Titulaire</Label>
            <Input value={details.holder_name || ''} onChange={(e) => setDetails({ ...details, holder_name: e.target.value })} className="mt-1 border-[#E8E6E1]" data-testid="bank-holder" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">IBAN</Label>
              <Input value={details.iban || ''} onChange={(e) => setDetails({ ...details, iban: e.target.value })} placeholder="FR76..." className="mt-1 border-[#E8E6E1] font-mono text-xs" data-testid="bank-iban" />
            </div>
            <div>
              <Label className="text-xs">BIC</Label>
              <Input value={details.bic || ''} onChange={(e) => setDetails({ ...details, bic: e.target.value })} className="mt-1 border-[#E8E6E1] font-mono text-xs" data-testid="bank-bic" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Banque</Label>
            <Input value={details.bank_name || ''} onChange={(e) => setDetails({ ...details, bank_name: e.target.value })} className="mt-1 border-[#E8E6E1]" data-testid="bank-name" />
          </div>
          <div className="flex gap-2 pt-2 border-t border-[#E8E6E1]">
            <Button onClick={save} disabled={saving || loading} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="bank-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Enregistrer
            </Button>
            <label className="inline-flex">
              <input type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadRib(e.target.files?.[0])} data-testid="bank-rib-input" />
              <Button type="button" variant="outline" className="border-[#E8E6E1]" disabled={uploading} onClick={(e) => e.currentTarget.previousSibling.click()} data-testid="bank-rib-upload-btn">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UploadSimple size={14} className="mr-2" />}
                {details.rib_path ? 'Remplacer RIB PDF' : 'Uploader RIB PDF'}
              </Button>
            </label>
            {details.rib_path && (
              <Button variant="outline" onClick={downloadRib} className="border-[#E8E6E1]" data-testid="bank-rib-download-btn">
                <Download size={14} className="mr-2" /> Télécharger RIB
              </Button>
            )}
          </div>
          {details.rib_path && (
            <p className="text-[10px] text-[#5E6D66]">RIB actuel : {details.rib_filename} ({Math.round((details.rib_size || 0) / 1024)} Ko)</p>
          )}
        </CardContent>
      </Card>

      <Card className="border-[#E8E6E1]">
        <CardContent className="p-4 space-y-3">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <PaperPlaneTilt size={16} className="text-[#2D5A4C]" />
            Envoyer les coordonnées + RIB
          </h3>
          <div>
            <Label className="text-xs">Destinataires (emails séparés par virgule)</Label>
            <Textarea rows={3} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="client1@x.fr, client2@y.fr" className="mt-1 border-[#E8E6E1] text-xs" data-testid="bank-send-emails" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Montant à virer (optionnel)</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 border-[#E8E6E1]" data-testid="bank-send-amount" />
            </div>
            <div>
              <Label className="text-xs">Motif</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Cotisation 2026" className="mt-1 border-[#E8E6E1]" data-testid="bank-send-description" />
            </div>
          </div>
          <Button onClick={send} disabled={sending || !details.iban} className="bg-[#2D5A4C] hover:bg-[#1E4136] w-full" data-testid="bank-send-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PaperPlaneTilt size={16} className="mr-2" />}
            Envoyer les coordonnées bancaires
          </Button>
          <p className="text-[10px] text-[#5E6D66]">Le RIB PDF est automatiquement joint à l'email si présent.</p>
        </CardContent>
      </Card>
    </div>
  );
}

function QuoteDialog({ lines, currency, title, dossierId, onClose }) {
  const [clientEmail, setClientEmail] = useState('');
  const [clientName, setClientName] = useState('');
  const [validityDays, setValidityDays] = useState(30);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);

  const validLines = useMemo(
    () => lines.filter((ln) => ln.description && Number(ln.unit_price_ht) > 0),
    [lines]
  );

  const handleCreate = async () => {
    if (!clientEmail.trim()) { toast.error('Email client requis'); return; }
    if (validLines.length === 0) { toast.error('Ajoutez au moins une ligne valide'); return; }
    setCreating(true);
    try {
      const r = await createQuote({
        title: title || 'Prestation O\'SCOP',
        currency,
        validity_days: Number(validityDays) || 30,
        dossier_id: dossierId || undefined,
        client_email: clientEmail.trim(),
        client_name: clientName.trim() || undefined,
        lines: validLines.map((l) => ({
          description: l.description,
          quantity: Number(l.quantity) || 1,
          unit_price_ht: Number(l.unit_price_ht),
          tva_rate: Number(l.tva_rate),
        })),
      });
      setCreated(r.data);
      toast.success(`Devis ${r.data.number} créé`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création devis');
    } finally { setCreating(false); }
  };

  const handleSend = async () => {
    if (!created) return;
    try {
      await sendQuote(created.id);
      toast.success('Devis envoyé au client par email');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="quote-dialog">
      <div className="bg-white rounded-xl border border-[#E8E6E1] max-w-lg w-full p-5 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[#1A2421] flex items-center gap-2">
            <FileText size={18} className="text-[#2D5A4C]" /> Générer un devis
          </h3>
          <button onClick={onClose} className="text-[#5E6D66] hover:text-[#1A2421]" data-testid="quote-dialog-close">✕</button>
        </div>

        {!created ? (
          <>
            <div>
              <Label className="text-xs">Email du client *</Label>
              <Input value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} type="email" placeholder="client@exemple.fr" className="mt-1 border-[#E8E6E1]" data-testid="quote-client-email" />
            </div>
            <div>
              <Label className="text-xs">Nom du client (optionnel)</Label>
              <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Société Exemple" className="mt-1 border-[#E8E6E1]" data-testid="quote-client-name" />
            </div>
            <div>
              <Label className="text-xs">Validité (jours)</Label>
              <Input type="number" min="1" max="365" value={validityDays} onChange={(e) => setValidityDays(e.target.value)} className="mt-1 border-[#E8E6E1] w-28" data-testid="quote-validity" />
            </div>
            <div className="bg-[#F9F8F5] border border-[#E8E6E1] rounded-md p-3">
              <p className="text-xs font-semibold text-[#5E6D66] uppercase tracking-wider mb-1">Récapitulatif ({validLines.length} ligne(s))</p>
              {validLines.map((ln, i) => (
                <p key={i} className="text-xs text-[#1A2421]">
                  {ln.description} — {ln.quantity} × {ln.unit_price_ht} HT + TVA {ln.tva_rate}%
                </p>
              ))}
            </div>
            <p className="text-[10px] text-[#5E6D66]">Le client recevra un email avec le PDF et un lien sécurisé pour consulter, signer et payer en ligne.</p>
            <Button onClick={handleCreate} disabled={creating} className="w-full bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="quote-create-submit">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText size={16} className="mr-2" />}
              Créer le devis
            </Button>
          </>
        ) : (
          <>
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-center">
              <Check size={28} className="mx-auto text-emerald-600 mb-1" />
              <p className="font-semibold text-emerald-800">Devis {created.number} créé</p>
              <p className="text-xs text-emerald-700">Valable jusqu'au {created.valid_until.slice(0, 10)}</p>
            </div>
            <div className="border border-[#E8E6E1] rounded-md p-3 bg-[#F9F8F5]">
              <p className="text-[10px] uppercase tracking-wider text-[#5E6D66] mb-1">Lien public client</p>
              <p className="text-xs font-mono break-all">{created.public_url}</p>
              <Button size="sm" variant="outline" className="mt-2 border-[#E8E6E1]" onClick={() => { navigator.clipboard.writeText(created.public_url); toast.success('Lien copié'); }} data-testid="quote-copy-link">
                <Link size={14} className="mr-1" /> Copier le lien
              </Button>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSend} className="flex-1 bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="quote-send-btn">
                <PaperPlaneTilt size={16} className="mr-2" /> Envoyer au client
              </Button>
              <Button variant="outline" onClick={onClose} className="border-[#E8E6E1]">Fermer</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const QUOTE_STATUS = {
  draft: { label: 'Brouillon', cls: 'bg-gray-100 text-gray-700' },
  sent: { label: 'Envoyé', cls: 'bg-blue-100 text-blue-800' },
  viewed: { label: 'Consulté', cls: 'bg-blue-100 text-blue-800' },
  accepted: { label: 'Accepté', cls: 'bg-emerald-100 text-emerald-800' },
  declined: { label: 'Refusé', cls: 'bg-red-100 text-red-800' },
  expired: { label: 'Expiré', cls: 'bg-amber-100 text-amber-800' },
};

function QuotesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listQuotes({ limit: 100 });
      setItems(r.data.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doView = async (q) => {
    try {
      const r = await viewQuote(q.id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Erreur ouverture'); }
  };
  const doDownload = async (q) => {
    try {
      const r = await downloadQuote(q.id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${q.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Erreur téléchargement'); }
  };
  const doSend = async (q) => {
    try {
      await sendQuote(q.id);
      toast.success(`Devis ${q.number} envoyé à ${q.client?.email}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur envoi'); }
  };
  const doExtend = async (q) => {
    try {
      await extendQuote(q.id, 30);
      toast.success(`Devis ${q.number} prolongé de 30 jours`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const doDelete = async (q) => {
    if (!window.confirm(`Supprimer le devis ${q.number} ?`)) return;
    try {
      await deleteQuote(q.id);
      toast.success('Devis supprimé');
      load();
    } catch { toast.error('Erreur suppression'); }
  };

  return (
    <Card className="border-[#E8E6E1]" data-testid="quotes-panel">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-wider text-[#5E6D66]">
            Devis ({items.length}) — créés depuis le mode « Détaillé » de l'onglet Lien Stripe
          </p>
          <Button variant="outline" size="sm" onClick={load} className="border-[#E8E6E1]" data-testid="reload-quotes-btn">
            <ArrowClockwise size={14} className="mr-1" /> Actualiser
          </Button>
        </div>
        {loading ? (
          <div className="py-8 text-center"><Loader2 className="animate-spin mx-auto text-[#2D5A4C]" /></div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-[#5E6D66]">
            <FileText size={28} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">Aucun devis. Créez-en un via l'onglet Lien Stripe → mode « Détaillé » → « Générer devis PDF ».</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E8E6E1] text-left text-xs uppercase tracking-wider text-[#5E6D66]">
                  <th className="py-2 pr-3">N°</th>
                  <th className="py-2 pr-3">Client</th>
                  <th className="py-2 pr-3">Objet</th>
                  <th className="py-2 pr-3">TTC</th>
                  <th className="py-2 pr-3">Validité</th>
                  <th className="py-2 pr-3">Statut</th>
                  <th className="py-2 pr-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((q) => (
                  <tr key={q.id} className="border-b border-[#E8E6E1] hover:bg-[#F9F8F5]" data-testid={`quote-row-${q.id}`}>
                    <td className="py-2 pr-3 font-mono text-xs">{q.number}</td>
                    <td className="py-2 pr-3">
                      <div className="text-xs">{q.client?.name}</div>
                      <div className="text-[10px] text-[#5E6D66]">{q.client?.email}</div>
                    </td>
                    <td className="py-2 pr-3 text-xs truncate max-w-[180px]">{q.title}</td>
                    <td className="py-2 pr-3 font-semibold">{formatMoney(q.total_ttc, (q.currency || 'EUR').toUpperCase())}</td>
                    <td className="py-2 pr-3 text-xs text-[#5E6D66]">{(q.valid_until || '').slice(0, 10)}</td>
                    <td className="py-2 pr-3">
                      <Badge className={`${QUOTE_STATUS[q.status]?.cls || 'bg-gray-100 text-gray-700'} text-[10px]`}>
                        {QUOTE_STATUS[q.status]?.label || q.status}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" title="Aperçu" onClick={() => doView(q)} data-testid={`view-quote-${q.id}`}><Eye size={14} /></Button>
                        <Button size="icon" variant="ghost" title="Télécharger" onClick={() => doDownload(q)} data-testid={`download-quote-${q.id}`}><Download size={14} /></Button>
                        <Button size="icon" variant="ghost" title="Copier lien public" onClick={() => { navigator.clipboard.writeText(q.public_url); toast.success('Lien copié'); }} data-testid={`copy-quote-${q.id}`}><Link size={14} /></Button>
                        {['draft', 'sent', 'viewed'].includes(q.status) && (
                          <Button size="icon" variant="ghost" title="Envoyer au client" onClick={() => doSend(q)} className="text-[#2D5A4C]" data-testid={`send-quote-${q.id}`}><PaperPlaneTilt size={14} /></Button>
                        )}
                        {q.status === 'expired' && (
                          <Button size="icon" variant="ghost" title="Prolonger 30j" onClick={() => doExtend(q)} className="text-amber-600" data-testid={`extend-quote-${q.id}`}><Clock size={14} /></Button>
                        )}
                        {q.status !== 'accepted' && (
                          <Button size="icon" variant="ghost" title="Supprimer" onClick={() => doDelete(q)} className="text-red-600" data-testid={`delete-quote-${q.id}`}><Trash size={14} /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

